import type Highcharts from "highcharts";
import React from "react";
import useSelectedAnalyses from "../../Analysis/useSelectedAnalyses";
import { color } from "@ui";
import { PlotlineLabelZIndex } from "@ui/Highcharts/ui/Wrapper";
import PlotLineLabel from "@ui/Highcharts/ui/PlotLineLabel";
import { zIndexChart } from "@ui/Highcharts/BetterHighcharts";
import useHoveredAnalysisId from "../../Analysis/useHoveredAnalysis";
import type { Period } from "@ui/Highcharts/hooks/usePeriod";
import useChartEventHandler from "@ui/Highcharts/hooks/useChartEventHandler";
import { closestSelectableAnalysis } from "../../Analysis/useSelectedAnalyses";
import { analysisTitleNoDate } from "../../Analysis/analysisTitle";
import type { Analysis } from "../../../../api/Analysis";
import useDebouncedCallback from "@ui/lib/hooks/useDebouncedCallback";
import { useSidePanelOpened } from "../../useSidePanel";

const DATE_RANGE_PLOTLINE_SELECTOR = "date-range-plotline-selector";
const HOVERED_DATE_RANGE_PLOTLINE_SELECTOR = "hovered-date-range-plotline-selector";
const DATE_RANGE_PLOTLBAND_ID = "date-range-plotband-id";
const LABEL_SIZE = 115;

export const CHART_SELECTION_PLOT_LINE_MARGIN = 50;
export const CHART_SELECTION_PLOT_LINE_MARGIN_NO_GROUP = 25;
const SELECTION_PLOT_LINE_Y = -38;

const plotBand = (from: number, to: number) => ({
  id: DATE_RANGE_PLOTLBAND_ID,
  color: color.lightNitrogen500_MainFade075,
  zIndex: zIndexChart.plotBands + 1,
  from,
  to,
});

const dateRangePlotLines = ({
  xAxis, // use for alignment
  type,
  date,
  endDate,
  isEnd,
  label,
  title,
  timeZone,
  noDataPoint,
}: {
  xAxis: Highcharts.Axis;
  type: "SELECTED" | "HOVER";
  date: number;
  endDate: number | null;
  isEnd: boolean;
  label: boolean;
  title: string | null;
  timeZone: string;
  noDataPoint: boolean;
}): Highcharts.XAxisPlotLinesOptions => {
  const isSelected = type === "SELECTED";

  const selectedColor = noDataPoint ? color.titanium400 : color.darkNitrogen600;
  const lightColor = isSelected ? selectedColor : color.darkNitrogen50;
  const { min, max } = xAxis.getExtremes();

  const startTimestamp = date;
  const endTimestamp = endDate === undefined ? false : endDate;

  const value = isEnd && endTimestamp ? endTimestamp : startTimestamp;

  const minPixels = xAxis.toPixels(min, true);
  const maxPixels = xAxis.toPixels(max, true);
  const startPixels = xAxis.toPixels(startTimestamp, true);

  const tooClose =
    endTimestamp && xAxis.toPixels(endTimestamp, true) - startPixels < 2 * LABEL_SIZE; // not enough space between boundaries to place 2 labels
  const tooClose1 = endTimestamp && xAxis.toPixels(endTimestamp, true) - startPixels < LABEL_SIZE; // not enought space between boundaries to place any label

  // first plotline is placed on right except :
  // - there is not enough place on right (isTooRight)
  // - second plotline is too close and there is place on left
  const labelCount = endDate === undefined ? 1 : 2;
  const isTooRight = maxPixels - startPixels < labelCount * LABEL_SIZE; // not enought space to place label on right
  const hasPlaceOnLeft = startPixels - minPixels > LABEL_SIZE;
  const startHorizontalAlign: "left" | "right" =
    isTooRight || (tooClose && hasPlaceOnLeft) ? "left" : "right";

  // second plotline is placed left except if it is too close to first plotline and there is place
  // on right
  const hasPlaceOnRight =
    endTimestamp && maxPixels - xAxis.toPixels(endTimestamp, true) > LABEL_SIZE;
  const endHorizontalAlign: "left" | "right" = tooClose && hasPlaceOnRight ? "right" : "left";
  const hasPostAnalysisAreaOnRight = maxPixels - startPixels > LABEL_SIZE;
  const horizontalAlign: "left" | "right" =
    noDataPoint && hasPostAnalysisAreaOnRight
      ? "right"
      : isEnd
        ? endHorizontalAlign
        : startHorizontalAlign;

  // hide label of second plotline when labels have same alignment and plolines are too close
  const hideLabel = isEnd && startHorizontalAlign === endHorizontalAlign && tooClose1;
  const hasLabel = label && !hideLabel;

  return {
    id: isSelected ? DATE_RANGE_PLOTLINE_SELECTOR : HOVERED_DATE_RANGE_PLOTLINE_SELECTOR,
    color: lightColor,
    dashStyle: "Dash",
    width: 2,
    value,
    zIndex: zIndexChart.plotLines,
    label: hasLabel
      ? {
          align: horizontalAlign === "left" ? "right" : "left",
          text: PlotLineLabel(title, isEnd && endDate !== null ? endDate : date, timeZone),
          rotation: 0,
          style: {
            padding: "5px",
            color: isSelected ? color.hydrogen : color.lightNitrogen500_Main,
            "background-color": lightColor,
            "border-radius": horizontalAlign === "right" ? "6px 6px 6px 0px" : "6px 6px 0px 6px",
            zIndex: PlotlineLabelZIndex.analysis,
          },
          verticalAlign: "top",
          y: ["cycle", "essai", "test"].some(w => title?.toLowerCase().includes(w))
            ? SELECTION_PLOT_LINE_Y
            : SELECTION_PLOT_LINE_Y / 2,
          x: horizontalAlign === "right" ? -1 : -9,
          useHTML: true,
        }
      : undefined,
  };
};

function getAnalysisAt(analyses: Analysis[], date: number) {
  return analyses.find(a => a.date === date) ?? null;
}

//
let previousAnalysis: Analysis | null = null;

const analysisTitle = (analysis: Analysis | null) => {
  if (analysis === null && previousAnalysis !== null) {
    return analysisTitleNoDate(previousAnalysis);
  }

  if (analysis === null) {
    return null;
  }

  previousAnalysis = analysis;
  return analysisTitleNoDate(analysis);
};

function isNoDataPoint(analysis: Analysis | null, boundaries: "active-analyses" | "analyses") {
  if (analysis === null) return false;
  if ("isPublished" in analysis) return false;
  return boundaries === "active-analyses";
}

const useAnalysisSelectionPlotLines = ({
  projectId,
  chartRef,
  boundaries,
  analyses,
  period,
  timeZone,
}: {
  projectId: string;
  chartRef: React.RefObject<Highcharts.Chart>;
  boundaries: "active-analyses" | "analyses";
  analyses: Analysis[];
  period: Period;
  timeZone: string;
}) => {
  const { boundary } = useSelectedAnalyses(projectId, analyses);
  const [sidePanelOpened] = useSidePanelOpened({ scope: projectId });

  const [hoveredAnalysisId, setHoveredAnalysisId] = useHoveredAnalysisId({});

  const getSelectableAnalysis = React.useCallback(
    (dateNumber: number) => closestSelectableAnalysis(analyses, dateNumber),
    [analyses],
  );

  const lastSelectableAnalysis = React.useRef<Analysis | null>(null);
  const isPointerInsidePlot = React.useRef(false);

  /**
   * @fixme performance workaround
   *
   * The highlight comment feature implies state updates
   * that somehow recompute all series. Even if we see a NO-UPDATE from smart update,
   * on very large datasets, series are still recomputed leading to 100% cpu usage (mbp m2).
   *
   * I think there is a architecture limitation in the way we handle hc interaction with series update.
   *
   * @note
   * Comment interaction should move plotlines and plotbands directly from graph ref instead of
   * updating shared state that causes the perf issue.
   *
   */
  const updateHoveredAnalysisId = useDebouncedCallback(
    (id: string | null) => {
      if (sidePanelOpened) setHoveredAnalysisId(id);
    },
    [setHoveredAnalysisId, sidePanelOpened],
    500,
  );

  // get lower and upper boundaries from boundary pivot and end
  const lowerBoundary =
    boundary.pivot === null
      ? null
      : boundary.to === null
        ? boundary.pivot
        : boundary.pivot <= boundary.to
          ? boundary.pivot
          : boundary.to;

  const upperBoundary =
    boundary.pivot === null
      ? null
      : boundary.to === null
        ? boundary.pivot
        : boundary.pivot > boundary.to
          ? boundary.pivot
          : boundary.to;

  React.useLayoutEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      throw new Error("chart not found");
    }
    const xAxis = chart.xAxis[0];
    const xAxisNavigator = chart.xAxis?.[1];
    if (!xAxis) throw new Error("Missing first xAxis");
    if (!xAxisNavigator) throw new Error("Missing xAxisNavigator");

    xAxis.removePlotLine(DATE_RANGE_PLOTLINE_SELECTOR);
    xAxisNavigator.removePlotLine(DATE_RANGE_PLOTLINE_SELECTOR);

    if (lowerBoundary === null || upperBoundary === null) return;

    const hasUpperBoundary = lowerBoundary !== upperBoundary;

    if (hasUpperBoundary) {
      const selectedAnalysis = getSelectableAnalysis(upperBoundary);
      const noDataPoint = isNoDataPoint(selectedAnalysis, boundaries);
      xAxis.addPlotLine(
        dateRangePlotLines({
          xAxis,
          type: "SELECTED",
          date: lowerBoundary,
          endDate: upperBoundary,
          isEnd: true,
          label: true,
          timeZone,
          title: analysisTitle(getAnalysisAt(analyses, upperBoundary)),
          noDataPoint,
        }),
      );

      xAxisNavigator.addPlotLine(
        dateRangePlotLines({
          xAxis,
          type: "SELECTED",
          date: lowerBoundary,
          endDate: upperBoundary,
          isEnd: true,
          label: false,
          timeZone,
          title: analysisTitle(getAnalysisAt(analyses, upperBoundary)),
          noDataPoint,
        }),
      );
    }

    xAxis.removePlotBand(DATE_RANGE_PLOTLBAND_ID);
    xAxisNavigator.removePlotBand(DATE_RANGE_PLOTLBAND_ID);
    if (lowerBoundary !== upperBoundary) {
      xAxis.addPlotBand(plotBand(lowerBoundary, upperBoundary));
      xAxisNavigator.addPlotBand(plotBand(lowerBoundary, upperBoundary));
    }

    const selectedAnalysis = getSelectableAnalysis(lowerBoundary);
    const noDataPoint = isNoDataPoint(selectedAnalysis, boundaries);

    const startLine = dateRangePlotLines({
      xAxis,
      type: "SELECTED",
      date: lowerBoundary,
      endDate: hasUpperBoundary ? upperBoundary : null,
      isEnd: false,
      label: true,
      timeZone,
      title: analysisTitle(getAnalysisAt(analyses, lowerBoundary)),
      noDataPoint,
    });
    xAxis.addPlotLine(startLine);

    // navigator
    xAxisNavigator.addPlotLine(
      dateRangePlotLines({
        xAxis,
        type: "SELECTED",
        date: lowerBoundary,
        endDate: null,
        isEnd: false,
        label: false,
        timeZone,
        title: analysisTitle(getAnalysisAt(analyses, lowerBoundary)),
        noDataPoint,
      }),
    );
  }, [
    chartRef,
    period,
    lowerBoundary,
    upperBoundary,
    timeZone,
    analyses,
    getSelectableAnalysis,
    boundaries,
  ]);

  React.useLayoutEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      throw new Error("chart not found");
    }
    const xAxis = chart.xAxis[0];
    const xAxisNavigator = chart.xAxis?.[1];
    if (!xAxis) throw new Error("Missing first xAxis");
    if (!xAxisNavigator) throw new Error("Missing xAxisNavigator");

    xAxis.removePlotLine(HOVERED_DATE_RANGE_PLOTLINE_SELECTOR);
    xAxisNavigator.removePlotLine(HOVERED_DATE_RANGE_PLOTLINE_SELECTOR);
    const hoveredAnalysis = analyses.find(analysis => analysis.id === hoveredAnalysisId);

    if (hoveredAnalysis === undefined || !isPointerInsidePlot.current) {
      updateHoveredAnalysisId(null);
    } else {
      if (hoveredAnalysis.date === lowerBoundary || hoveredAnalysis.date === upperBoundary) return;

      const selectedAnalysis = getSelectableAnalysis(hoveredAnalysis.date);
      const noDataPoint = isNoDataPoint(selectedAnalysis, boundaries);

      xAxis.addPlotLine(
        dateRangePlotLines({
          xAxis,
          type: "HOVER",
          date: hoveredAnalysis.date,
          endDate: null,
          isEnd: false,
          label: true,
          timeZone,
          title: analysisTitle(hoveredAnalysis),
          noDataPoint,
        }),
      );
      xAxisNavigator.addPlotLine(
        dateRangePlotLines({
          xAxis,
          type: "HOVER",
          date: hoveredAnalysis.date,
          endDate: null,
          isEnd: false,
          label: false,
          timeZone,
          title: analysisTitle(hoveredAnalysis),
          noDataPoint,
        }),
      );
    }
    // period needed to switch horizontalAlign
  }, [
    analyses,
    chartRef,
    hoveredAnalysisId,
    lowerBoundary,
    timeZone,
    updateHoveredAnalysisId,
    upperBoundary,
    getSelectableAnalysis,
    boundaries,
  ]);

  const handleChartMousemove = React.useCallback(
    function (this: Highcharts.Chart, e: Highcharts.PointerEventObject) {
      const xAxis = this.xAxis[0];
      const xAxisNavigator = this.xAxis?.[1];
      if (!xAxis) throw new Error("Missing xAxis");
      if (!xAxisNavigator) throw new Error("Missing xAxis navigator");

      // remove plotline and do not change it when dragging
      if (e.buttons || this.hoverPoint) {
        updateHoveredAnalysisId(null);
        return;
      }

      const x = xAxis.toValue(e.chartX);
      const y = e.chartY;
      const minY = this.plotTop;
      const maxY = this.plotTop + this.plotHeight;
      const minChartX = this.plotLeft;
      const maxChartX = this.plotLeft + this.plotWidth;
      const insideAxes =
        e.chartX >= minChartX && e.chartX <= maxChartX && y >= minY - 40 && y <= maxY;
      if (!insideAxes) {
        isPointerInsidePlot.current = false;
        xAxis.removePlotLine(HOVERED_DATE_RANGE_PLOTLINE_SELECTOR);
        xAxisNavigator.removePlotLine(HOVERED_DATE_RANGE_PLOTLINE_SELECTOR);
        updateHoveredAnalysisId(null);
        lastSelectableAnalysis.current = null;
        return;
      }
      isPointerInsidePlot.current = true;
      const hoveredAnalysis = getSelectableAnalysis(x);

      if (hoveredAnalysis === null) {
        // empty chart
        updateHoveredAnalysisId(null);
        return;
      }

      if (hoveredAnalysis.id === lastSelectableAnalysis.current?.id) return;

      xAxis.removePlotLine(HOVERED_DATE_RANGE_PLOTLINE_SELECTOR);
      xAxisNavigator.removePlotLine(HOVERED_DATE_RANGE_PLOTLINE_SELECTOR);

      if (lowerBoundary !== hoveredAnalysis.date && upperBoundary !== hoveredAnalysis.date) {
        const selectedAnalysis = getSelectableAnalysis(hoveredAnalysis.date);
        const noDataPoint = isNoDataPoint(selectedAnalysis, boundaries);

        xAxis.addPlotLine(
          dateRangePlotLines({
            xAxis,
            type: "HOVER",
            date: hoveredAnalysis.date,
            endDate: null,
            isEnd: false,
            label: true,
            timeZone,
            title: analysisTitle(hoveredAnalysis),
            noDataPoint,
          }),
        );
        xAxisNavigator.addPlotLine(
          dateRangePlotLines({
            xAxis,
            type: "HOVER",
            date: hoveredAnalysis.date,
            endDate: null,
            isEnd: false,
            label: false,
            timeZone,
            title: analysisTitle(hoveredAnalysis),
            noDataPoint,
          }),
        );
        updateHoveredAnalysisId(hoveredAnalysis.id);
        lastSelectableAnalysis.current = hoveredAnalysis;
        return;
      }
    },
    [
      getSelectableAnalysis,
      lowerBoundary,
      timeZone,
      updateHoveredAnalysisId,
      upperBoundary,
      boundaries,
    ],
  );

  React.useEffect(() => {
    const container = chartRef.current?.container;
    if (!container) return;
    const handleMouseLeave = () => {
      isPointerInsidePlot.current = false;
      const chart = chartRef.current;
      const xAxis = chart?.xAxis?.[0];
      const xAxisNavigator = chart?.xAxis?.[1];
      xAxis?.removePlotLine(HOVERED_DATE_RANGE_PLOTLINE_SELECTOR);
      xAxisNavigator?.removePlotLine(HOVERED_DATE_RANGE_PLOTLINE_SELECTOR);
      updateHoveredAnalysisId(null);
      lastSelectableAnalysis.current = null;
    };
    container.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      container.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [chartRef, updateHoveredAnalysisId]);
  useChartEventHandler(
    chartRef,
    "mousemove",
    handleChartMousemove as Highcharts.EventCallbackFunction<Highcharts.Chart>,
  );
};

export default useAnalysisSelectionPlotLines;
